import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext.js";
import "./styles/AdminDashboard.css";

const AdminPricing = () => {
  const { token } = useContext(AuthContext);
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({});
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const fetchPlans = async () => {
    try {
      const resp = await fetch("/api/admin/subscription-plans", {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!resp.ok) throw new Error("Failed to fetch plans");
      const data = await resp.json();
      setPlans(data);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const startEditing = (plan) => {
    setEditing(plan.id);
    setFormData({
      price: (plan.price_cents / 100).toFixed(2),
      special_price: plan.special_price_cents ? (plan.special_price_cents / 100).toFixed(2) : "",
      special_label: plan.special_label || "",
      active: plan.active
    });
    setError(null);
    setSuccess(null);
  };

  const cancelEditing = () => {
    setEditing(null);
    setFormData({});
  };

  const handleSave = async (planId) => {
    setError(null);
    setSuccess(null);

    const price_cents = Math.round(parseFloat(formData.price) * 100);
    if (isNaN(price_cents) || price_cents <= 0) {
      setError("Please enter a valid price");
      return;
    }

    let special_price_cents = null;
    if (formData.special_price && formData.special_price.trim() !== "") {
      special_price_cents = Math.round(parseFloat(formData.special_price) * 100);
      if (isNaN(special_price_cents) || special_price_cents <= 0) {
        setError("Please enter a valid special price or leave it empty");
        return;
      }
    }

    try {
      const resp = await fetch(`/api/admin/subscription-plans/${planId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          price_cents,
          special_price_cents,
          special_label: formData.special_label.trim() || null,
          active: formData.active
        })
      });

      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.message || "Failed to update plan");
      }

      setSuccess("Plan updated successfully!");
      setEditing(null);
      fetchPlans();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="admin-layout">
      <div className="admin-layout-inner">
        <div className="admin-page-header">
          <h1>Subscription Pricing</h1>
          <p>Manage subscription plans, prices, and special offers</p>
        </div>

        {error && <div className="admin-alert admin-alert-error">{error}</div>}
        {success && <div className="admin-alert admin-alert-success">{success}</div>}

        <div className="admin-card">
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Billing</th>
                  <th>Price</th>
                  <th>Special Price</th>
                  <th>Special Label</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((plan) => (
                  <tr key={plan.id}>
                    {editing === plan.id ? (
                      <>
                        <td style={{ fontWeight: 500 }}>{plan.name}</td>
                        <td>{plan.interval_label}</td>
                        <td>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.price}
                            onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                            className="admin-input admin-input-sm"
                            style={{ width: "90px" }}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.special_price}
                            onChange={(e) => setFormData({ ...formData, special_price: e.target.value })}
                            placeholder="None"
                            className="admin-input admin-input-sm"
                            style={{ width: "90px" }}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={formData.special_label}
                            onChange={(e) => setFormData({ ...formData, special_label: e.target.value })}
                            placeholder="e.g. $2.99 for first 6 months"
                            className="admin-input admin-input-sm"
                            style={{ width: "200px" }}
                          />
                        </td>
                        <td>
                          <select
                            value={formData.active ? "true" : "false"}
                            onChange={(e) => setFormData({ ...formData, active: e.target.value === "true" })}
                            className="admin-input admin-input-sm"
                            style={{ width: "80px" }}
                          >
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                          </select>
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <button onClick={() => handleSave(plan.id)} className="admin-btn admin-btn-success">Save</button>
                            <button onClick={cancelEditing} className="admin-btn admin-btn-ghost">Cancel</button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={{ fontWeight: 500 }}>{plan.name}</td>
                        <td>{plan.interval_label}</td>
                        <td>${(plan.price_cents / 100).toFixed(2)}</td>
                        <td>{plan.special_price_cents ? `$${(plan.special_price_cents / 100).toFixed(2)}` : "—"}</td>
                        <td style={{ color: plan.special_label ? "var(--admin-text)" : "var(--admin-text-muted)" }}>{plan.special_label || "—"}</td>
                        <td>
                          <span className={`admin-badge admin-badge-${plan.active ? "active" : "inactive"}`}>
                            {plan.active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          <button onClick={() => startEditing(plan)} className="admin-btn admin-btn-primary">Edit</button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
                {plans.length === 0 && (
                  <tr>
                    <td colSpan="7" className="admin-empty">No subscription plans found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPricing;
